import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { User } from '@appsvio/jira-rest-api-client';
import EditorRemoveIcon from '@atlaskit/icon/glyph/editor/remove';
import { router } from '@forge/bridge';

import { IncidentBannerDto } from '../../../../../src/services/incident-banners/model';
import { MappedIssue } from '../interfaces';
import { handleBannerDeletion, updateEditData } from '../store/incidentBannersSlice';
import { AppDispatch, RootState } from '../store/store';

import { BannerName, CreatedSection, IssueInfo, IssueLink, TopSection, Wrapper } from './BannerStyled';

interface Props {
    data: IncidentBannerDto;
    issues: MappedIssue[];
    siteUrl: string;
    users: User[];
}

const getUserDisplayName = (accountId: string, users: User[]) =>
    users.find(user => user.accountId === accountId)?.displayName || accountId;

const getIssueKey = (issueId: string, issues: MappedIssue[]) =>
    issues.find(issue => issue.issueId === issueId)?.issueKey || issueId;

const getIssueLink = (issueId: string, siteUrl: string, issues: MappedIssue[]) =>
    `${siteUrl}/browse/${getIssueKey(issueId, issues)}`;

const getFormattedDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };

    // @ts-ignore
    return new Intl.DateTimeFormat('en-US', options).format(date);
};

const Banner: React.FC<Props> = ({ data, issues, siteUrl, users }) => {
    const { canEdit } = useSelector((state: RootState) => state.incidentBanners);
    const dispatch = useDispatch<AppDispatch>();
    const { createAccountId, createdAt, issueId, name, updatedAt, updateAccountId, projectId, key } = data;

    const formattedCreatedAt = useMemo(() => getFormattedDate(createdAt), [createdAt]);
    const formattedUpdatedAt = useMemo(() => (updatedAt ? getFormattedDate(updatedAt) : null), [updatedAt]);
    const createdBy = useMemo(() => getUserDisplayName(createAccountId, users), [createAccountId, users]);
    const updatedBy = useMemo(
        () => (updateAccountId ? getUserDisplayName(updateAccountId, users) : null),
        [updateAccountId, users]
    );
    const issueKey = useMemo(() => getIssueKey(issueId, issues), [issueId, issues]);
    const issueLink = useMemo(() => getIssueLink(issueId, siteUrl, issues), [issueId, siteUrl, issues]);

    const handleDelete = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(handleBannerDeletion(key));
        },
        [dispatch, key, projectId]
    );

    const handleIssueClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            router.open(issueLink);
        },
        [issueLink]
    );

    return (
        <Wrapper
            canEdit={canEdit as boolean}
            onClick={() => dispatch(updateEditData({ activeBannerKey: key, editData: data }))}
        >
            <TopSection>
                <BannerName>{name}</BannerName>
                {canEdit && (
                    <div
                        className='delete'
                        onClick={handleDelete}
                    >
                        <EditorRemoveIcon
                            size='medium'
                            label='remove'
                        />
                    </div>
                )}
            </TopSection>
            <div>
                <IssueInfo>
                    <CreatedSection>
                        Created {formattedCreatedAt} by {createdBy} from &nbsp;
                        <IssueLink onClick={handleIssueClick}>{issueKey}</IssueLink>
                    </CreatedSection>
                    {updatedAt && updateAccountId && (
                        <div>
                            Updated {formattedUpdatedAt} by {updatedBy}
                        </div>
                    )}
                </IssueInfo>
            </div>
        </Wrapper>
    );
};

export default Banner;
